/* eslint-disable camelcase -- these keys arrive from the API in snake_case */
/* eslint-disable react/jsx-props-no-spreading -- wrappers here forward props wholesale */
import { board, ui, select } from '@owenscorning/pcb.alpha';
import _ from 'lodash';
import { useState, useEffect } from 'react';
import ReactOnRails from 'react-on-rails';
import { Provider } from 'react-redux';
import ResizeObserver from 'resize-observer-polyfill';

import Library from './Library';
import Postie from './Postie';
import joinPath from '../../helpers/joinPath';
import GlobalThemeStyles from '../../themes/globalThemeStyles';
// connecting to redux manually for now
import { cmsRenderConnector } from '../PageBuilder/redux/connector';
import { navigate } from '../helpers/sticky_nav';
import { connectLocation } from '../location/LocationConnectors';

const Renderer = board`Builder::Renderer`('0.0.1')(
  Library,
  ({ editable, isComparison }) => {
    const Editor = {};

    if (editable) {
      Editor.bridge = Postie.Child();
      Editor.bridge.on('select', selection => Board.select?.(selection, false));
      Editor.bridge.on('data', data => Editor.receive?.(data));
      Editor.bridge.send('ready');
    } else if (isComparison) {
      Editor.bridge = Postie.Child();
      Editor.bridge.send('ready');
    }

    return { Editor };
  },
  ({
    Editor,
    editable,
    locale: { code: language },
    contentType,
    content_info,
    displayContents,
    metadata,
    linkedObject,
    isComparison,
    zipFile,
  }) => {
    const Type = _.get(Library.Types, contentType.replace('Cms::Content::', '').split('::'))(Library);

    // TODO: use the localized name (from content_info.language_summary should be already passed)
    const Content = {
      name: content_info?.name,
      info: content_info,
    };

    const { view, variables, ...definition } = Type;
    const build = {
      type: contentType,
      name: Content.name,
      site: Content.info?.site,
      location: Content.info?.path,
      slug: Content.info?.slug,
      path: joinPath(Content.info?.path, Content.info?.slug),
      language,
    };

    const pageContents = _.cloneDeep(displayContents) || {};
    const pageMetadata = _.cloneDeep(metadata) || {};

    const constructedType = Type.read({
      build,
      contents: pageContents,
      metadata: pageMetadata,
      zipFile,
    });
    const [value, setValue] = editable ? useState(constructedType) : [constructedType, () => {}];
    Editor.receive = setValue;

    if (editable || isComparison) {
      useEffect(() => {
        const observer = new ResizeObserver(([body]) => Editor.bridge.send('height', body.target.offsetHeight));
        observer.observe(document.getElementsByTagName('html')[0]);
        return () => observer.disconnect();
      }, []);
    } else if (typeof window !== 'undefined') {
      const { adobeDataLayer } = window;
      if (adobeDataLayer && digitalData) {
        const store = ReactOnRails.getStore('Store');
        const { featureFlag, location } = store.getState();
        let analyticsInitData = {
          event: 'init',
          page: {
            page_name: digitalData.page?.pageName,
            referring_page_name: digitalData.page?.referringPageName,
            referring_url: digitalData.page?.referringUrl,
          },
          site: {
            language: digitalData.site?.language,
            site_domain: digitalData.site?.siteDomain,
            site_name: digitalData.site?.siteName,
          },
          enabled_feature_flags: featureFlag?.enabledFeatureFlags || [],
        };
        if (digitalData.user?.hashedAcct) {
          // check how to verify if user is present
          analyticsInitData = Object.assign(analyticsInitData, {
            visitor: {
              city: location?.address?.city,
              country: location?.address?.country,
              country_iso: location?.address?.country_iso,
              region: location?.address?.region,
              time_zone: location?.address?.time_zone,
              zip_code: location?.address?.zip_code,
              zip_code_entered: location?.zip,
              zone: location?.address?.zone,
            },
          });
        }
        adobeDataLayer.push(analyticsInitData);
      }
      useEffect(() => {
        if (window.location.hash) {
          const hash = window.location.hash.substring(1);
          navigate(hash);
        }
      }, []);
    }

    return {
      value: Library.User.Filter(value),
      variables: _.merge(
        {},
        {
          editable,
          definition,
          language,
          bridge: Editor.bridge,
          settings: value.metadata?.settings || {},
          build,
          selection: null,
          select: (selection, push = true) => {
            if (!editable) return;
            if (push) Editor.bridge.send('select', selection);
            const element = document.getElementById(selection);
            if (element)
              Editor.bridge.send(
                'position',
                element.getBoundingClientRect().top + window.scrollY
              );
            Board.Set({ selection });
          },
        },
        variables
      ),
      schematic: {
        modals: ui`Modals`({ open: select`~modal/stack` }),
        ...view,
      },
    };
  }
);

const ConnectedRenderer = cmsRenderConnector(connectLocation(Renderer));
const ProvidedRenderer = props => {
  const store = ReactOnRails.getStore('Store');
  return (
    <Provider store={store}>
      <GlobalThemeStyles />
      <ConnectedRenderer {...props} />
    </Provider>
  );
};

export default ProvidedRenderer;
